import React, { useState, useEffect } from 'react';
import { FaTimes, FaSearch } from 'react-icons/fa';
import { useI18n } from '../../i18n';
import './ForwardMeetingModal.css';
import { notify } from '../CustomToast';
import userService from '../../services/userService';
import meetingService from '../../services/meetingService';
import RepeatIcon from '../../assets/Repeat.svg';
import ForwardMeetingModalSkeleton from './ForwardMeetingModalSkeleton';

const ForwardMeetingModal = ({ isOpen, onClose, meeting }) => {
    const { t } = useI18n();
    const [selection, setSelection] = useState('single');
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [usersLoading, setUsersLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        if (isOpen) {
            fetchUsers();
        } else {
            // Reset when closed
            setSelectedUser(null);
            setUsers([]);
            setSearchQuery('');
        }
    }, [isOpen, meeting]);

    const fetchUsers = async () => {
        setUsersLoading(true);
        try {
            const response = await userService.list();
            let data = response.data;
            if (data && data.status === true && data.data) {
                data = data.data;
            }
            const allUsers = Array.isArray(data) ? data : (data?.users || []);
            const meetingMembersIds = meeting?.members?.map(m => String(m.id)) || [];
            const organizerId = String(meeting?.organizer?.id);

            const availableUsers = allUsers.filter(u =>
                !meetingMembersIds.includes(String(u.id)) && String(u.id) !== organizerId
            );
            setUsers(availableUsers);
        } catch (err) {
            console.error('Error fetching users:', err);
        } finally {
            setUsersLoading(false);
        }
    };

    const resetForm = () => {
        setSelectedUser(null);
        setSelection('single');
        setSearchQuery('');
        setUsers([]);
    };

    const filteredUsers = users.filter(u =>
        u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (u.email && u.email.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    if (!isOpen) return null;

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const handleConfirm = async () => {
        if (!selectedUser) {
            notify('error', t('pages.meetings.select_user_error') || 'Please select a user');
            return;
        }

        const scope = selection === 'single' ? 'just_this_one' : 'entire_series';

        try {
            await meetingService.createForwardRequest(meeting.id, selectedUser.id, scope);
            notify('success', t('pages.meetings.forward_request_sent') || 'Forward request sent to organizer');
            resetForm();
            onClose();
        } catch (err) {
            if (err.status === 422 && err.errors) {
                const errorMsg = err.errors.invitee_id?.[0] ||
                               err.errors.scope?.[0] ||
                               err.message ||
                               'Failed to create forward request';
                notify('error', errorMsg);
            } else {
                notify('error', err.message || 'Failed to create forward request');
            }
        }
    };

    return (
        <div className="fm-modal-overlay" onClick={handleBackdropClick}>
            <div className="fm-modal">
                <button
                    className="fm-modal-close"
                    onClick={onClose}
                    aria-label="Close"
                >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>

                <div className="fm-modal-header">
                    <div className="fm-modal-header-left">
                        <img
                            src={RepeatIcon}
                            alt=""
                            className="fm-modal-small-icon fm-modal-icon-tint"
                        />
                        <h2 className="fm-modal-title">{t('pages.meetings.forward_meeting') || 'Forward Meeting'}</h2>
                    </div>
                </div>

                <p className="fm-modal-description">
                    {t('pages.meetings.forward_series_desc') || 'This is one appointment in a series. What do you want to open?'}
                </p>

                <div className="fm-modal-radio-group">
                    <label className="fm-radio-label">
                        <input 
                            type="radio" 
                            name="forward_type" 
                            value="single" 
                            checked={selection === 'single'} 
                            onChange={() => setSelection('single')} 
                        />
                        <span className="fm-radio-text">{t('pages.meetings.just_this_one') || 'Just this one'}</span>
                    </label>
                    <label className="fm-radio-label">
                        <input 
                            type="radio" 
                            name="forward_type" 
                            value="series" 
                            checked={selection === 'series'} 
                            onChange={() => setSelection('series')} 
                        />
                        <span className="fm-radio-text">{t('pages.meetings.entire_series') || 'The entire series'}</span>
                    </label>
                </div>

                <div className="fm-modal-user-group">
                    <label className="fm-modal-user-label">{t('pages.meetings.forward_to') || 'Forward to'}</label>
                    {selectedUser ? (
                        <div className="fm-selected-item">
                            <div>
                                <div className="fm-selected-name">{selectedUser.name}</div>
                                <div className="fm-selected-email">{selectedUser.email}</div>
                            </div>
                            <button type="button" className="fm-clear-btn" onClick={() => setSelectedUser(null)}>
                                <FaTimes />
                            </button>
                        </div>
                    ) : (
                        <div className="fm-user-picker">
                            {usersLoading ? (
                                <ForwardMeetingModalSkeleton />
                            ) : (
                                <>
                                    <div className="fm-search-row">
                                        <FaSearch className="fm-search-icon" />
                                        <input
                                            type="text"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            placeholder={t('common.actions.search') || 'Search...'}
                                            className="fm-search-input"
                                            autoFocus
                                        />
                                    </div>
                                    <div className="fm-user-list">
                                        {filteredUsers.length === 0 ? (
                                            <div className="fm-picker-empty">{t('common.empty_states.no_results') || 'No results found'}</div>
                                        ) : filteredUsers.map(user => (
                                            <div
                                                key={user.id}
                                                className="fm-picker-item"
                                                onClick={() => {
                                                    setSelectedUser(user);
                                                    setSearchQuery('');
                                                }}
                                            >
                                                <div className="fm-user-avatar">
                                                    {user.avatar_url
                                                        ? <img src={user.avatar_url} alt={user.name} />
                                                        : user.name.charAt(0).toUpperCase()}
                                                </div>
                                                <div>
                                                    <div className="fm-picker-item-name">{user.name}</div>
                                                    <div className="fm-picker-item-email">{user.email}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>

                <div className="fm-modal-actions">
                    <button
                        className="fm-modal-cancel-btn"
                        onClick={onClose}
                    >
                        {t('common.actions.cancel') || 'Cancel'}
                    </button>
                    <button
                        className="fm-modal-confirm-btn"
                        onClick={handleConfirm}
                    >
                        {t('common.actions.okay') || 'Okay'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ForwardMeetingModal;
